// src/bootstrap/nativeInterceptor.ts
type HttpMethod = string | undefined;
type CartOrigin = 'theme' | 'react' | 'network-interceptor' | 'init' | 'trigger';

const WRITE_PATH_PATTERNS = [
  /\/cart\/add/i,
  /\/cart\/change/i,
  /\/cart\/clear/i,
  /\/cart\/update/i,
  /\/cart\.js/i
];

const SOURCE_PARAM = '_source=react';

declare global {
  interface Window {
    refreshCartData?: (origin?: CartOrigin | 'init') => void;
  }
}

let initialized = false;

/**
 * 判断请求是否是第三方/主题侧写入购物车的请求，需要触发 React 同步。
 * @param url 需要检测的请求地址，可以是字符串或 URL 对象
 * @param method HTTP 方法名，未提供时默认视为 GET
 * @returns 是否需要刷新购物车数据
 */
function shouldRefresh(url: string | URL, method?: HttpMethod) {
  const normalizedMethod = (method ?? 'GET').toUpperCase();
  if (normalizedMethod === 'GET') return false;
  const target = typeof url === 'string' ? url : url.toString();
  if (target.includes(SOURCE_PARAM)) return false;
  return WRITE_PATH_PATTERNS.some((pattern) => pattern.test(target));
}

/** 轻量延迟触发 refreshCartData，避免阻塞原始请求 */
function scheduleRefresh() {
  const refresh = window.refreshCartData;
  if (typeof refresh !== 'function') return;
  window.setTimeout(() => refresh('network-interceptor'), 300);
}

/** 给 fetch 打补丁，在非 React 来源的写请求后触发刷新 */
function patchFetch() {
  const originalFetch = window.fetch;
  window.fetch = async (...args: Parameters<typeof fetch>) => {
    const response = await originalFetch(...args);
    try {
      const [input, init] = args;
      const url = typeof input === 'string' || input instanceof URL ? input : input.url;
      const method = (init as RequestInit | undefined)?.method || (input instanceof Request ? input.method : undefined);
      if (shouldRefresh(url, method)) {
        scheduleRefresh();
      }
    } catch (err) {
      console.warn('nativeInterceptor: failed to inspect fetch request', err);
    }
    return response;
  };
}

/** 给 XHR 打补丁，兼容主题侧旧代码 */
function patchXHR() {
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;
  type TrackingXHR = XMLHttpRequest & { __cartShouldTrack__?: boolean };

  XMLHttpRequest.prototype.open = function (this: TrackingXHR, method: string, url: string | URL, ...rest: any[]) {
    this.__cartShouldTrack__ = shouldRefresh(url, method);
    const async = (rest[0] ?? true) as boolean;
    const username = rest[1] as string | undefined;
    const password = rest[2] as string | undefined;
    return originalOpen.call(this, method, url, async, username, password);
  };

  XMLHttpRequest.prototype.send = function (this: TrackingXHR, ...args: any[]) {
    if (this.__cartShouldTrack__) {
      this.addEventListener('loadend', scheduleRefresh);
    }
    const [body] = args as [Document | XMLHttpRequestBodyInit | null | undefined];
    return originalSend.call(this, body);
  };
}

/** 初始化原生拦截器，确保 React 对外部写操作保持一致 */
export function initNativeInterceptor() {
  if (initialized) return;
  if (typeof window === 'undefined') return;

  patchFetch();
  patchXHR();

  initialized = true;
}
